import { Check, X } from "lucide-react";
import DateInput from "react-calendar-ui-kit";
import type {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import { getMaxDate } from "../../utils/Date";
import CauseAdvocatesSection from "./CauseAdvocatesSection";
import CausePartiesSection from "./CausePartiesSection";
import CauseCourtFields from "./CauseCourtFields";
import type { CauseFormData } from "./causeList.types";
import type {
  CourtOptionPaginationControls,
  SearchOption,
} from "../../types/searchSessions";

type CauseCourtSelectedLabels = Partial<
  Record<
    | "highcourt"
    | "state"
    | "bench"
    | "benchRoom"
    | "district"
    | "complex"
    | "establishment"
    | "courtRoom",
    string
  >
>;

interface AddCauseModalProps {
  register: UseFormRegister<CauseFormData>;
  handleSubmit: UseFormHandleSubmit<CauseFormData>;
  setValue: UseFormSetValue<CauseFormData>;
  watch: UseFormWatch<CauseFormData>;
  control: Control<CauseFormData>;
  errors: FieldErrors<CauseFormData>;
  onClose: () => void;
  onSubmit: (data: CauseFormData) => void;
  isSubmitting: boolean;
  caseTypes: string[];
  caseStages: string[];
  highCourts: SearchOption[];
  states: SearchOption[];
  benches: SearchOption[];
  benchRooms: SearchOption[];
  districts: SearchOption[];
  complexes: SearchOption[];
  establishments: SearchOption[];
  courtRooms: SearchOption[];
  courtEstablishment?: boolean;
  courtOptionControls?: CourtOptionPaginationControls;
  selectedLabels?: CauseCourtSelectedLabels;
}

const AddCauseModal = ({
  register,
  handleSubmit,
  setValue,
  watch,
  control,
  errors,
  onClose,
  onSubmit,
  isSubmitting,
  caseTypes,
  caseStages,
  highCourts,
  states,
  benches,
  benchRooms,
  districts,
  complexes,
  establishments,
  courtRooms,
  courtEstablishment,
  courtOptionControls,
  selectedLabels,
}: AddCauseModalProps) => {
  const district = watch("district");
  const state = watch("state");
  const complex = watch("complex");
  const highcourt = watch("highcourt");
  const bench = watch("bench");
  const benchRoom = watch("benchRoom");
  const establishment = watch("establishment");
  const courtRoom = watch("courtRoom");
  const courtType = watch("courtType");

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-2xl font-bold text-gray-800">
            Add New Case to Cause List
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            type="button"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8">
          <CaseIdentificationSection register={register} errors={errors} />
          <PartiesSection
            register={register}
            control={control}
            setValue={setValue}
          />
          <CaseDetailsSection
            register={register}
            setValue={setValue}
            watch={watch}
            caseTypes={caseTypes}
            caseStages={caseStages}
            errors={errors}
          />
          <CauseCourtFields
            control={control}
            setValue={setValue}
            errors={errors}
            courtType={courtType}
            highcourt={highcourt}
            state={state}
            bench={bench}
            benchRoom={benchRoom}
            district={district}
            complex={complex}
            establishment={establishment}
            courtRoom={courtRoom}
            highCourts={highCourts}
            states={states}
            benches={benches}
            benchRooms={benchRooms}
            districts={districts}
            complexes={complexes}
            establishments={establishments}
            courtRooms={courtRooms}
            courtEstablishment={courtEstablishment}
            controls={courtOptionControls}
            selectedLabels={selectedLabels}
          />

          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-linear-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Adding Case...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Add to Cause List
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface CaseIdentificationSectionProps {
  register: UseFormRegister<CauseFormData>;
  errors: FieldErrors<CauseFormData>;
}

const CaseIdentificationSection = ({
  register,
  errors,
}: CaseIdentificationSectionProps) => (
  <FormSection title="Case Identification">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <FormInput
        label="CNR Number*"
        placeholder="DLCT01123456789A"
        error={errors.cnr?.message}
        registerProps={register("cnr", {
          required: "CNR Number is required",
          maxLength: {
            value: 16,
            message: "CNR Number must be 16 characters or fewer",
          },
        })}
        maxLength={16}
      />
      <FormInput
        label="Case Number"
        placeholder="CN/2024/9014"
        registerProps={register("caseNumber")}
        maxLength={20}
      />
      <FormInput
        label="Filing Number"
        placeholder="CA/2024/001235"
        registerProps={register("filingNumber")}
        maxLength={20}
      />
      <FormInput
        label="Registration Number"
        placeholder="REG/2024/5679"
        registerProps={register("registrationNumber")}
        maxLength={12}
      />
    </div>
  </FormSection>
);

// interface PartiesSectionProps {
//   register: UseFormRegister<CauseFormData>;
//   control: Control<CauseFormData>;
//   setValue: UseFormSetValue<CauseFormData>;
// }

// const PartiesSection = ({
//   register,
//   control,
//   setValue,
// }: PartiesSectionProps) => (
//   <FormSection title="Parties Information">
//     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//       <FormInput
//         label="Petitioner Name"
//         placeholder="Enter petitioner name"
//         registerProps={register("petitioner")}
//       />
//       <FormInput
//         label="Respondent Name"
//         placeholder="Enter respondent name"
//         registerProps={register("respondent")}
//       />
//       <CauseAdvocatesSection
//         control={control}
//         register={register}
//         setValue={setValue}
//       />
//     </div>
//   </FormSection>
// )

interface PartiesSectionProps {
  register: UseFormRegister<CauseFormData>;
  control: Control<CauseFormData>;
  setValue: UseFormSetValue<CauseFormData>;
}

const PartiesSection = ({
  register,
  control,
  setValue,
}: PartiesSectionProps) => (
  <FormSection title="Parties Information">
    <div className="grid grid-cols-1 gap-6">
      <CausePartiesSection control={control} register={register} />

      <CauseAdvocatesSection
        control={control}
        register={register}
        setValue={setValue}
      />
    </div>
  </FormSection>
);

interface CaseDetailsSectionProps {
  register: UseFormRegister<CauseFormData>;
  setValue: UseFormSetValue<CauseFormData>;
  watch: UseFormWatch<CauseFormData>;
  caseTypes: string[];
  caseStages: string[];
  errors: FieldErrors<CauseFormData>;
}

const CaseDetailsSection = ({
  register,
  setValue,
  watch,
  caseTypes,
  caseStages,
  errors,
}: CaseDetailsSectionProps) => {
  const filingDate = watch("filingDate");
  const registrationDate = watch("registrationDate");
  console.log("registeratuion date", registrationDate);
  console.log("fillingdate", filingDate);

  const getMinDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <FormSection title="Case Details">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Case Type
          </label>
          <select {...register("caseType")} className={inputClassName}>
            <option value="">Select case type</option>
            {caseTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Case Stage
          </label>
          <select {...register("caseStage")} className={inputClassName}>
            <option value="">Select case stage</option>
            {caseStages.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </select>
        </div>

        <DateField
          label="Filing Date"
          value={watch("filingDate")}
          onChange={(value) => {
            setValue("filingDate", value, { shouldValidate: true });
            setValue("registrationDate", watch("registrationDate"), {
              shouldValidate: true,
            });
          }}
        />
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Registration Date
          </label>
          {/* Hidden input carries the value into RHF for validation */}
          <input
            type="hidden"
            {...register("registrationDate", {
              validate: (value) => {
                if (!value || !filingDate) return true;
                return (
                  value >= filingDate ||
                  "Registration Date must be on or after Filing Date"
                );
              },
            })}
          />
          <DateInput
            value={registrationDate}
            onChange={(val: string) =>
              setValue("registrationDate", val, { shouldValidate: true })
            }
            min={filingDate || undefined}
            max={getMaxDate()}
            theme={{
              selectWidth: "140px",
              yearButtonWidth: "120px",
              inputPadding: "12px 12px",
            }}
          />
          {errors.registrationDate && (
            <p className="text-red-500 text-sm mt-1">
              {errors.registrationDate.message as string}
            </p>
          )}
        </div>
        <DateField
          label="Hearing Date"
          value={watch("hearingDate")}
          min={getMinDate()}
          onChange={(value) => setValue("hearingDate", value)}
        />
      </div>
    </FormSection>
  );
};

interface FormSectionProps {
  title: string;
  children: React.ReactNode;
}

const FormSection = ({ title, children }: FormSectionProps) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
      {title}
    </h3>
    {children}
  </div>
);

interface FormInputProps {
  label: string;
  placeholder: string;
  error?: string;
  registerProps: any;
  maxLength?: number;
}

const FormInput = ({
  label,
  placeholder,
  error,
  registerProps,
  maxLength,
}: FormInputProps) => (
  <div>
    <label className="block text-sm font-semibold text-gray-900 mb-2">
      {label}
    </label>
    <input
      {...registerProps}
      className={
        error
          ? "w-full px-4 py-3 border border-red-500 rounded-xl focus:ring-1 focus:ring-red-400 outline-0 transition-colors"
          : inputClassName
      }
      placeholder={placeholder}
      maxLength={maxLength}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

interface DateFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  error?: string;
}

const DateField = ({ label, value, onChange, min, error }: DateFieldProps) => (
  <div>
    <label className="block text-sm font-semibold text-gray-900 mb-2">
      {label}
    </label>
    <DateInput
      value={value}
      min={min}
      onChange={onChange}
      max={getMaxDate()}
      theme={{
        selectWidth: "140px",
        yearButtonWidth: "120px",
        inputPadding: "12px 12px",
      }}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const inputClassName =
  "w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-1 focus:ring-blue-400 outline-0 focus:border-blue-400 transition-colors";

export default AddCauseModal;
